import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { BimChapterEnum, BimWorkOrderChangePriceData, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import {
    SmrGroupHeaderComponent,
    SmrTotalHeaderComponent,
    CodeCellRendererComponent,
    bimNumberFormatter,
} from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { cellWithDifferentFiled } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/cellRenderers';
import type { ITotalFieldParams } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks';
import {
    TotalHeaderComponent,
    totalLeftSideGetter,
} from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import {
    bimWorkOrderChangePriceCodeFilterGql,
    bimWorkOrderChangePriceNameFilterGql,
    bimWorkOrderChangePriceObjectNameFilterGql,
    bimWorkOrderChangePriceRationaleFilterGql,
} from '../bimWorkOrderChangePriceQueries';

import { useWorkOrderChangeMenuColumn } from './useWorkOrderChangeMenuColumn';

const rightAlignedCellClassName = 'ag-right-aligned-cell';

export const getWorkOrderChangeValueFields = ({
    valueKey,
    marryChildren = true,
    withCollapse = true,
    useBoldStyle = false,
    totalFieldParams,
}: {
    valueKey: 'completedValue' | 'planValue' | 'remainderValue' | 'value';
    marryChildren?: boolean;
    withCollapse?: boolean;
    useBoldStyle?: boolean;
    totalFieldParams?: ITotalFieldParams;
}): (ColDef | ColGroupDef)[] => {
    const keys = {
        smrTotal: `${valueKey}.smrTotal`,
        ozp: `${valueKey}.ozp`,
        emm: `${valueKey}.emm`,
        including: `${valueKey}.including`,
        zpm: `${valueKey}.zpm`,
        mp: `${valueKey}.mp`,
        nr: `${valueKey}.nr`,
        sp: `${valueKey}.sp`,
        op: `${valueKey}.op`,
        others: `${valueKey}.others`,
        total: `${valueKey}.total`,
        mz: `${valueKey}.mz`,
        oz: `${valueKey}.oz`,
        tzr: `${valueKey}.tzr`,
    };

    const cellStyle = `${rightAlignedCellClassName} ${useBoldStyle ? 'boldWeight' : ''}`;

    return [
        {
            headerName: 'Стоимость строительно-монтажных работ',
            headerGroupComponent: SmrGroupHeaderComponent,
            width: 600,
            children: [
                {
                    headerName: 'Всего СМР',
                    field: keys.smrTotal,
                    width: 140,
                    valueFormatter: bimNumberFormatter(0),
                    headerComponent: withCollapse ? SmrTotalHeaderComponent : null,
                    headerComponentParams: {
                        workOrderStatus: WorkOrderStatusEnum.InProgress,
                        collapseKeys: [keys.ozp, keys.emm, keys.including, keys.zpm, keys.mp, keys.nr, keys.sp],
                    },
                    cellClass: cellStyle,
                },
                {
                    headerName: 'ОЗП',
                    field: keys.ozp,
                    width: 120,
                    hide: withCollapse,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: cellStyle,
                },
                {
                    headerName: 'ЭММ',
                    field: keys.emm,
                    width: 120,
                    hide: withCollapse,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: cellStyle,
                },
                {
                    headerName: 'В том числе',
                    field: keys.including,
                    width: 120,
                    hide: withCollapse,
                    children: [
                        {
                            headerName: 'ЗПМ',
                            field: keys.zpm,
                            width: 180,
                            hide: withCollapse,
                            valueFormatter: bimNumberFormatter(0),
                            cellClass: cellStyle,
                        },
                    ],
                    marryChildren,
                },
                {
                    headerName: 'МП',
                    field: keys.mp,
                    width: 120,
                    hide: withCollapse,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: cellStyle,
                },
                {
                    headerName: 'НР',
                    field: keys.nr,
                    width: 120,
                    hide: withCollapse,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: cellStyle,
                },
                {
                    headerName: 'СП',
                    field: keys.sp,
                    width: 120,
                    hide: withCollapse,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: cellStyle,
                },
            ],
        },
        {
            headerName: 'ОП',
            field: keys.op,
            width: 120,
            valueFormatter: bimNumberFormatter(0),
            cellClass: cellStyle,
        },
        {
            headerName: 'Прочие затраты',
            field: keys.others,
            width: 120,
            valueFormatter: bimNumberFormatter(0),
            cellClass: cellStyle,
        },
        {
            headerName: 'Всего',
            field: keys.total,
            width: 120,
            valueFormatter: bimNumberFormatter(0),
            cellClass: cellStyle,
            cellRenderer: cellWithDifferentFiled('total', keys.total, totalFieldParams?.leftSideGetter),
            headerComponent: totalFieldParams?.HeaderComponent,
        },
        {
            headerName: 'Справочно МЗ',
            field: keys.mz,
            width: 140,
            valueFormatter: bimNumberFormatter(0),
            cellClass: cellStyle,
        },
        {
            headerName: 'Справочно ОЗ',
            field: keys.oz,
            width: 140,
            valueFormatter: bimNumberFormatter(0),
            cellClass: cellStyle,
        },
        {
            headerName: 'ТЗР, чел.час',
            field: keys.tzr,
            width: 120,
            valueFormatter: bimNumberFormatter(2),
            cellClass: cellStyle,
        },
    ];
};

interface IBimWorkOrderChangeColumns {
    workOrderChangeId: number;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    chapter: BimChapterEnum;
    changeFilterHandler: ({
        fieldName,
        checkedValues,
        fullDataSet,
    }: {
        fieldName: string;
        checkedValues: any[];
        fullDataSet: any[];
    }) => void;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    hasExcessCompletedTotalSMR: boolean;
    refreshTable: () => void;
}

export const useBimWorkOrderChangeColumns = ({
    workOrderChangeId,
    workOrderRootObjectId,
    workOrderChangeRootObjectId,
    chapter,
    changeFilterHandler,
    workOrderChangeStatus,
    hasExcessCompletedTotalSMR,
    refreshTable,
}: IBimWorkOrderChangeColumns) => {
    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            chapter,
        }),
        [chapter, workOrderChangeId, workOrderChangeRootObjectId, workOrderRootObjectId],
    );

    const {
        menuColumn,
        checkedRows,
        resetCheckedRows,
        checkedDetailsRow,
        handlerCloseDetails,
        detailsType,
        detailsRowType,
    } = useWorkOrderChangeMenuColumn({
        workOrderChangeStatus,
        refreshTable,
    });

    const columns = useMemo<(ColDef<BimWorkOrderChangePriceData> | ColGroupDef<BimWorkOrderChangePriceData>)[]>(
        () => [
            menuColumn,
            {
                headerName: 'Подобъект',
                field: 'object.name',
                suppressMovable: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                lockPosition: 'left',
                valueFormatter: (props) => {
                    const { data, value } = props;

                    if (data?.isLimitPrice) {
                        return '';
                    }

                    return value ?? '';
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderChangePriceObjectNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подобъект',
                            field: 'value',
                            width: 400,
                            checkboxSelection: true,
                        },
                    ],
                    query: bimWorkOrderChangePriceObjectNameFilterGql,
                },
            },
            {
                headerName: 'Шифр',
                field: 'planValue.code',
                width: 230,
                suppressMovable: true,
                lockPosition: 'left',
                cellRenderer: CodeCellRendererComponent,
                cellRendererParams: {
                    field: 'name',
                    dotNotationField: ['planValue.code', 'completedValue.code'],
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderChangePriceCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            width: 400,
                            checkboxSelection: true,
                        },
                    ],
                    query: bimWorkOrderChangePriceCodeFilterGql,
                },
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'planValue.name',
                width: 180,
                suppressMovable: true,
                lockPosition: 'left',
                filter: AgGridCustomFilterAdapter,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderChangePriceNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование работ, затрат',
                            field: 'value',
                            width: 400,
                            checkboxSelection: true,
                        },
                    ],
                    query: bimWorkOrderChangePriceNameFilterGql,
                },
            },
            {
                headerName: 'Ед. изм',
                field: 'planValue.unit',
                width: 100,
            },
            {
                headerName: 'Кол-во',
                field: 'planValue.quantity',
                width: 100,
            },
            {
                headerName: 'План по НЗ',
                children: getWorkOrderChangeValueFields({
                    valueKey: 'planValue',
                }),
                marryChildren: true,
            },
            {
                headerName: 'Выполнено по НЗ',
                children: getWorkOrderChangeValueFields({
                    valueKey: 'completedValue',
                    totalFieldParams: {
                        HeaderComponent: hasExcessCompletedTotalSMR ? TotalHeaderComponent : undefined,
                        leftSideGetter: totalLeftSideGetter,
                    },
                }),
                marryChildren: true,
            },
            {
                headerName: 'Остаток по НЗ',
                children: getWorkOrderChangeValueFields({
                    valueKey: 'remainderValue',
                }),
                marryChildren: true,
            },
            {
                headerName: 'Обоснование',
                field: 'rationale',
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderChangePriceRationaleFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            width: 400,
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Без обоснования>',
                        },
                    ],
                    query: bimWorkOrderChangePriceRationaleFilterGql,
                },
            },
        ],
        [changeFilterHandler, filterVariables, hasExcessCompletedTotalSMR, menuColumn],
    );
    const [selection, , , setSelection] = useSelection(`bimWorkOrderChangeTable_${workOrderChangeId}`);
    const [pinnedItems, , , setPinned] = usePin(`bimWorkOrderChangeTable_${workOrderChangeId}`);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);
    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 4) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);

                    return acc;
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );
    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        checkedRows,
        resetCheckedRows,
        checkedDetailsRow,
        detailsType,
        handlerCloseDetails,
        detailsRowType,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
